import { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import api from "../lib/api";
import { createCoordFilter } from "../lib/coordFilter";

/**
 * High-accuracy GPS tracker with confidence-based filtering.
 *
 * Strategy:
 *   - Always request enableHighAccuracy: true (forces GPS over cell-tower/IP)
 *   - Bypass cache (maximumAge: 0) so the OS keeps refining
 *   - Reject fixes with accuracy > MAX_ACCEPTABLE_METERS unless we have nothing better yet
 *   - Prefer the best fix from the last 30 seconds (don't downgrade to a worse one)
 *   - Send to backend every `interval` ms only if a fresh, accepted fix is available
 *   - Surface permission state so the UI can prompt the user to enable Precise Location
 */
const MAX_ACCEPTABLE_METERS = 100;       // Reject worse than this if we have anything better
const FORCED_ACCEPT_METERS = 500;        // Never reject if we'd send nothing at all
const BEST_FIX_WINDOW_MS = 30000;        // Keep the best fix for this long

export default function LocationTracker({ enabled = true, interval = 15000, onUpdate, requestKey = 0 }) {
  const lastSent = useRef(0);
  const bestFix = useRef(null);
  const watchId = useRef(null);
  const filter = useRef(createCoordFilter());
  const [permission, setPermission] = useState("prompt");

  useEffect(() => {
    if (!("permissions" in navigator) || !navigator.permissions.query) return;
    navigator.permissions
      .query({ name: "geolocation" })
      .then((status) => {
        setPermission(status.state);
        status.onchange = () => setPermission(status.state);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!enabled || !("geolocation" in navigator)) return;
    // On native Android/iOS the BackgroundGeolocation plugin owns location
    // (see useNativeTelemetry.js). Running the browser watchPosition here
    // in parallel would cause duplicate /locations/update uploads and
    // stop the OS from putting the WebView to sleep. Never substitute
    // this browser path for the native background tracker.
    if (Capacitor?.isNativePlatform?.()) return;
    // Do not wake Android's permission sheet before the dashboard has explained
    // why location is needed. A granted permission resumes automatically; a
    // requestKey change is the explicit foreground-location consent action.
    if (permission !== "granted" && requestKey === 0) return;

    const accept = (pos) => {
      setPermission("granted");
      // Run the raw browser fix through the adaptive low-pass filter
      // BEFORE anything else uses it. This is the single choke-point:
      // every coordinate — UI marker AND server upload — is smoothed.
      const filtered = filter.current.smooth(pos);
      if (!filtered) return;

      // Feed the SMOOTHED value to consumers as a GeolocationPosition-shaped
      // object so downstream (Dashboard `me` state) doesn't need to change.
      const smoothedPos = {
        coords: {
          latitude: filtered.lat,
          longitude: filtered.lng,
          accuracy: filtered.accuracy,
          heading: pos.coords?.heading ?? null,
          speed: pos.coords?.speed ?? null,
        },
        timestamp: pos.timestamp ?? Date.now(),
      };
      onUpdate?.(smoothedPos);

      const a = filtered.accuracy;

      // Server-send gate: prefer best recent fix
      const now = Date.now();
      const best = bestFix.current;
      const bestStale = best && now - best.timestamp > BEST_FIX_WINDOW_MS;
      const shouldReplaceBest =
        !best || bestStale || a < (best.coords?.accuracy ?? 9999);
      if (shouldReplaceBest) {
        bestFix.current = {
          coords: {
            latitude: filtered.lat,
            longitude: filtered.lng,
            accuracy: filtered.accuracy,
            heading: pos.coords?.heading ?? null,
            speed: pos.coords?.speed ?? null,
          },
          timestamp: now,
        };
      }

      // Reject high-error fix when we have a recent good one
      const useFix = bestFix.current;
      const acc = useFix.coords?.accuracy ?? 9999;
      const haveAnythingRecent =
        useFix && now - useFix.timestamp < BEST_FIX_WINDOW_MS;
      if (acc > MAX_ACCEPTABLE_METERS && haveAnythingRecent) {
        if (now - lastSent.current < interval * 3) return;
        if (acc > FORCED_ACCEPT_METERS) return;
      }

      if (now - lastSent.current < interval) return;
      lastSent.current = now;
      api.post("/locations/update", {
        lat: useFix.coords.latitude,
        lng: useFix.coords.longitude,
        accuracy: useFix.coords.accuracy,
        heading: useFix.coords.heading,
        speed: useFix.coords.speed,
        source: "gps",
      }).catch(() => {});
    };

    const onError = (err) => {
      // PERMISSION_DENIED=1, POSITION_UNAVAILABLE=2, TIMEOUT=3
      if (err?.code === 1) setPermission("denied");
    };

    // Continuous watch — OS will refine GPS over time
    watchId.current = navigator.geolocation.watchPosition(
      accept,
      onError,
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 }
    );

    // Immediate one-shot to get a fix faster
    navigator.geolocation.getCurrentPosition(accept, onError, {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 10000,
    });

    return () => {
      if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current);
    };
  }, [enabled, interval, onUpdate, permission, requestKey]);

  return null;
}

/** Hook for components to read current permission state. */
export function useLocationPermission() {
  const [state, setState] = useState("prompt");
  useEffect(() => {
    if (!navigator.permissions?.query) return;
    let active = true;
    navigator.permissions
      .query({ name: "geolocation" })
      .then((status) => {
        if (!active) return;
        setState(status.state);
        status.onchange = () => active && setState(status.state);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);
  return state;
}
